import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import './Subscriptions.css';
import SubscriptionImportWizard from './SubscriptionImportWizard';
import AddSubscriptionForm from './AddSubscriptionForm';
import SubscriptionToProjectWizard from './subscription/SubscriptionToProjectWizard';

interface Subscription {
    id: number;
    description: string | null;
    annualPrice: number; // cents
    taxRate: number; // percent
    ownerRoundTableId: number | null;
    ownerRoundTable?: { id: number; name: string; number: number } | null;
    mostRecentRechnungId: number | null;
    createdAt: string;
}

interface ActionMenuState {
    [key: number]: boolean; // subscriptionId -> isMenuOpen
}

interface EditingState {
    id: number;
    description: string | null;
    annualPrice: string; // euros for display
    taxRate: string;
    ownerRoundTableId: number | null;
}

interface RoundTable {
    id: number;
    name: string;
    number: number;
}

const SubscriptionsComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    if (!authContext) throw new Error('SubscriptionsComponent must be used within AuthProvider');
    if (!navigationContext) throw new Error('SubscriptionsComponent must be used within NavigationProvider');
    const { token } = authContext;
    const { navigateTo } = navigationContext;

    const [loading, setLoading] = useState(true);
    const [subs, setSubs] = useState<Subscription[]>([]);
    const [showImportWizard, setShowImportWizard] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showProjectWizard, setShowProjectWizard] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [rtdFilter, setRtdFilter] = useState<string>('');
    const [sortField, setSortField] = useState<keyof Subscription>('id');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [actionMenuOpen, setActionMenuOpen] = useState<ActionMenuState>({});
    const [editingRow, setEditingRow] = useState<EditingState | null>(null);
    const [roundTables, setRoundTables] = useState<RoundTable[]>([]);

    const loadSubscriptions = async () => {
        try {
            const res = await fetch('/api/subscriptions', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Fehler beim Laden der Abonnements');
            const data = await res.json();
            setSubs(data);
        } catch (e) {
            console.error(e);
            toast.error(e instanceof Error ? e.message : 'Unbekannter Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    const loadRoundTables = async () => {
        try {
            const res = await fetch('/api/roundtables', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Fehler beim Laden der Round Tables');
            const data = await res.json();
            setRoundTables(data);
        } catch (e) {
            console.error(e);
            toast.error('Fehler beim Laden der Round Tables');
        }
    };

    useEffect(() => { loadSubscriptions(); }, [token]);

    // Load round tables for the edit dropdown
    useEffect(() => {
        loadRoundTables();
    }, [token]);

    // Close action menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.action-menu-container')) {
                setActionMenuOpen({});
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const formatPrice = (cents: number) => {
        return `${(cents / 100).toFixed(2)} €`;
    };

    const openImportWizard = () => setShowImportWizard(true);
    const closeImportWizard = () => setShowImportWizard(false);

    const openAddForm = () => setShowAddForm(true);
    const closeAddForm = () => setShowAddForm(false);
    const handleAddSuccess = () => {
        loadSubscriptions();
        toast.success('Abonnement erfolgreich hinzugefügt');
    };

    const openProjectWizard = () => setShowProjectWizard(true);
    const closeProjectWizard = () => setShowProjectWizard(false);
    const handleProjectWizardSuccess = (projectId: number) => {
        closeProjectWizard();
        toast.success('Projekt erfolgreich erstellt');
        // Navigate to the project details page
        navigateTo(`/projectdetails/${projectId}`);
    };

    const toggleActionMenu = (subscriptionId: number) => {
        setActionMenuOpen(prev => ({
            ...prev,
            [subscriptionId]: !prev[subscriptionId]
        }));
    };

    const handleDetailsClick = (subscriptionId: number) => {
        setActionMenuOpen({});
        toast.info('Details-Ansicht ist noch nicht implementiert');
    };

    const handleDeleteClick = async (subscriptionId: number) => {
        setActionMenuOpen({});
        
        if (!window.confirm('Möchten Sie dieses Abonnement wirklich löschen?')) {
            return;
        }

        try {
            const res = await fetch(`/api/subscriptions/${subscriptionId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!res.ok) {
                throw new Error('Fehler beim Löschen des Abonnements');
            }
            
            toast.success('Abonnement erfolgreich gelöscht');
            loadSubscriptions();
        } catch (e) {
            console.error(e);
            toast.error(e instanceof Error ? e.message : 'Unbekannter Fehler beim Löschen');
        }
    };

    const handleEditClick = (subscription: Subscription) => {
        setActionMenuOpen({});
        setEditingRow({
            id: subscription.id,
            description: subscription.description,
            annualPrice: (subscription.annualPrice / 100).toFixed(2),
            taxRate: subscription.taxRate.toString(),
            ownerRoundTableId: subscription.ownerRoundTableId,
        });
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
    };

    const handleSaveEdit = async () => {
        if (!editingRow) return;

        // Validation
        const priceInCents = Math.round(parseFloat(editingRow.annualPrice) * 100);
        if (isNaN(priceInCents) || priceInCents <= 0) {
            toast.error('Bitte geben Sie einen gültigen positiven Jahrespreis ein');
            return;
        }

        const taxRateNum = parseInt(editingRow.taxRate);
        if (isNaN(taxRateNum) || taxRateNum < 0 || taxRateNum > 100) {
            toast.error('Bitte geben Sie einen gültigen Steuersatz (0-100) ein');
            return;
        }

        try {
            const res = await fetch(`/api/subscriptions/${editingRow.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    description: editingRow.description || null,
                    annualPrice: priceInCents,
                    taxRate: taxRateNum,
                    ownerRoundTableId: editingRow.ownerRoundTableId,
                }),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Fehler beim Aktualisieren des Abonnements');
            }

            toast.success('Abonnement erfolgreich aktualisiert');
            setEditingRow(null);
            loadSubscriptions();
        } catch (e) {
            console.error(e);
            toast.error(e instanceof Error ? e.message : 'Unbekannter Fehler beim Aktualisieren');
        }
    };

    const clearSearchAndFilters = () => {
        setSearchTerm('');
        setRtdFilter('');
    };

    // Helper function to check if subscription matches RTD search
    const matchesRtd = (sub: Subscription, searchText: string): boolean => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        const rtdNameMatch = sub.ownerRoundTable && sub.ownerRoundTable.name.toLowerCase().includes(searchLower);
        const rtdNumberMatch = sub.ownerRoundTable && sub.ownerRoundTable.number.toString().includes(searchLower);
        return !!(rtdNameMatch || rtdNumberMatch);
    };

    // Filter logic
    const filteredSubs = subs.filter((sub) => {
        // Apply search term (free text search in description and RTD)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const descriptionMatch = sub.description && sub.description.toLowerCase().includes(searchLower);
            const rtdMatch = matchesRtd(sub, searchTerm);
            
            if (!descriptionMatch && !rtdMatch) {
                return false;
            }
        }

        // Apply RTD filter
        if (rtdFilter && !matchesRtd(sub, rtdFilter)) {
            return false;
        }

        return true;
    });

    // Sorting logic
    const sortedSubs = [...filteredSubs].sort((a, b) => {
        let aValue: string | number | null | undefined = a[sortField];
        let bValue: string | number | null | undefined = b[sortField];

        // Handle special case for ownerRoundTable
        if (sortField === 'ownerRoundTableId') {
            aValue = a.ownerRoundTable?.number ?? Number.MAX_SAFE_INTEGER;
            bValue = b.ownerRoundTable?.number ?? Number.MAX_SAFE_INTEGER;
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
        if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

        // Compare values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.localeCompare(bValue);
            return sortDirection === 'asc' ? comparison : -comparison;
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
    });

    const handleSort = (field: keyof Subscription) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field with ascending direction
            setSortField(field);
            setSortDirection('asc');
        }
    };

    if (loading) {
        return <div className="subscriptions-container"><div className="subscriptions-loading">Lade Abonnements...</div></div>;
    }

    return (
        <div className="subscriptions-container">
            <div className="subscriptions-header">
                <h1>Abonnements</h1>
                <div className="subscriptions-actions">
                    <button className="add-button" onClick={openAddForm} aria-label="Abonnement hinzufügen">+ Hinzufügen</button>
                    <button className="project-button" onClick={openProjectWizard} aria-label="Projekt aus Abonnements erstellen">📋 Zu Projekt</button>
                    {/* <button className="import-button" onClick={openImportWizard} aria-label="Import Wizard öffnen">Import</button> */}
                </div>
            </div>

            <div className="search-filter-section">
                <div className="search-bar">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Suche in Beschreibung und RTD..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>
                            ✕
                        </button>
                    )}
                </div>
                
                <button 
                    className="filter-toggle-button"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    🔽 Filter
                </button>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-row">
                        <label>
                            RTD:
                            <input
                                type="text"
                                placeholder="z.B. Berlin oder 123"
                                value={rtdFilter}
                                onChange={(e) => setRtdFilter(e.target.value)}
                            />
                        </label>
                        <button className="clear-filters-button" onClick={clearSearchAndFilters}>
                            Filter zurücksetzen
                        </button>
                    </div>
                </div>
            )}

            {subs.length === 0 ? (
                <p className="no-data">Keine Abonnements vorhanden</p>
            ) : filteredSubs.length === 0 ? (
                <p className="no-data">Keine Abonnements entsprechen den Filterkriterien</p>
            ) : (
                <table className="subscriptions-table">
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => handleSort('id')}>
                                ID {sortField === 'id' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('description')}>
                                Beschreibung {sortField === 'description' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('annualPrice')}>
                                Jahrespreis {sortField === 'annualPrice' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('taxRate')}>
                                Steuersatz {sortField === 'taxRate' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('ownerRoundTableId')}>
                                RTD {sortField === 'ownerRoundTableId' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('mostRecentRechnungId')}>
                                Neueste Rechnung {sortField === 'mostRecentRechnungId' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('createdAt')}>
                                Erstellt {sortField === 'createdAt' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedSubs.map(s => {
                            const isEditing = editingRow?.id === s.id;
                            
                            if (isEditing && editingRow) {
                                return (
                                    <tr key={s.id} className="editing-row">
                                        <td>{s.id}</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={editingRow.description || ''}
                                                onChange={(e) => setEditingRow({ ...editingRow, description: e.target.value || null })}
                                                placeholder="Beschreibung"
                                            />
                                        </td>
                                        <td>
                                            <div className="edit-price-input">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="edit-input"
                                                    value={editingRow.annualPrice}
                                                    onChange={(e) => setEditingRow({ ...editingRow, annualPrice: e.target.value })}
                                                />
                                                <span className="currency-symbol-inline">€</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="edit-tax-input">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    className="edit-input"
                                                    value={editingRow.taxRate}
                                                    onChange={(e) => setEditingRow({ ...editingRow, taxRate: e.target.value })}
                                                />
                                                <span className="percent-symbol-inline">%</span>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                className="edit-select"
                                                value={editingRow.ownerRoundTableId || ''}
                                                onChange={(e) => setEditingRow({ ...editingRow, ownerRoundTableId: e.target.value ? parseInt(e.target.value) : null })}
                                            >
                                                <option value="">Keine Auswahl</option>
                                                {roundTables.map(rt => (
                                                    <option key={rt.id} value={rt.id}>
                                                        {rt.name} (RT {rt.number})
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>{s.mostRecentRechnungId ? `#${s.mostRecentRechnungId}` : '—'}</td>
                                        <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                                        <td className="action-cell">
                                            <div className="edit-actions">
                                                <button 
                                                    className="edit-action-button edit-save"
                                                    onClick={handleSaveEdit}
                                                    aria-label="Speichern"
                                                    title="Speichern"
                                                >
                                                    ✓
                                                </button>
                                                <button 
                                                    className="edit-action-button edit-cancel"
                                                    onClick={handleCancelEdit}
                                                    aria-label="Abbrechen"
                                                    title="Abbrechen"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            
                            return (
                                <tr key={s.id}>
                                    <td>{s.id}</td>
                                    <td>{s.description || '—'}</td>
                                    <td>{formatPrice(s.annualPrice)}</td>
                                    <td>{s.taxRate}%</td>
                                    <td>{s.ownerRoundTable ? `${s.ownerRoundTable.name} (RT ${s.ownerRoundTable.number})` : '—'}</td>
                                    <td>{s.mostRecentRechnungId ? `#${s.mostRecentRechnungId}` : '—'}</td>
                                    <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                                    <td className="action-cell">
                                        <div className="action-menu-container">
                                            <button 
                                                className="action-menu-button"
                                                onClick={() => toggleActionMenu(s.id)}
                                                aria-label="Aktionen"
                                            >
                                                ⋮
                                            </button>
                                            {actionMenuOpen[s.id] && (
                                                <div className="action-dropdown">
                                                    <button onClick={() => handleDetailsClick(s.id)}>
                                                        Details
                                                    </button>
                                                    <button onClick={() => handleEditClick(s)}>
                                                        Bearbeiten
                                                    </button>
                                                    <button onClick={() => handleDeleteClick(s.id)} className="delete-action">
                                                        Löschen
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {showImportWizard && (
                <SubscriptionImportWizard
                    modal
                    onClose={closeImportWizard}
                    onImported={(count) => { loadSubscriptions(); toast.success(`${count} Abonnements importiert`); }}
                />
            )}
            {showAddForm && (
                <AddSubscriptionForm
                    onClose={closeAddForm}
                    onSuccess={handleAddSuccess}
                />
            )}
            {showProjectWizard && (
                <SubscriptionToProjectWizard
                    onClose={closeProjectWizard}
                    onSuccess={handleProjectWizardSuccess}
                    token={token}
                />
            )}
        </div>
    );
};

export default SubscriptionsComponent;
